"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { logger } from "@utilities/logger";

import { type CreateAudioReturn, createAudio } from "../BlockInteractCreateOmnibox/createAudio";
import { AudioRow } from "./AudioRow";
import type { AudioListItem } from "./BlockIconLinkList";

type PlayState = "idle" | "loading" | "playing" | "paused";

interface BlockIconLinkListAudioProps {
  items: AudioListItem[];
  listClassName: string;
}

export function BlockIconLinkListAudio({ items, listClassName }: BlockIconLinkListAudioProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [playState, setPlayState] = useState<PlayState>("idle");
  const audioRef = useRef<CreateAudioReturn | null>(null);

  const cleanup = useCallback(() => {
    audioRef.current?.cleanup();
    audioRef.current = null;
    setAnalyser(null);
    setPlayState("idle");
  }, []);

  const playTrack = useCallback(
    (item: AudioListItem) => {
      cleanup();

      const audio = createAudio({
        src: item.audioUrl,
        fade: false,
        onLoaded: ({ analyser: nextAnalyser }) => {
          setAnalyser(nextAnalyser);
          audioRef.current?.play();
        },
        onPlayStarted: () => {
          setPlayState("playing");
        },
        onEnded: () => {
          setPlayState("idle");
        },
        onError: error => {
          logger.error(null, "[BlockIconLinkListAudio] Audio playback error", { error, src: item.audioUrl });
          setPlayState("idle");
        },
      });

      audioRef.current = audio;
      setPlayState("loading");
      audio.load();
    },
    [cleanup],
  );

  const handleToggle = useCallback(
    (index: number) => {
      const isSameTrack = index === selectedIndex;
      setSelectedIndex(index);

      if (!isSameTrack) {
        playTrack(items[index]);
        return;
      }

      if (playState === "playing") {
        audioRef.current?.pause();
        setPlayState("paused");
        return;
      }

      if (playState === "paused" && audioRef.current) {
        audioRef.current.play();
        setPlayState("playing");
        return;
      }

      if (playState === "loading") {
        cleanup();
        return;
      }

      playTrack(items[index]);
    },
    [items, selectedIndex, playState, playTrack, cleanup],
  );

  useEffect(() => cleanup, [cleanup]);

  const isPlaying = playState === "playing";

  return (
    <ul role='list' className={listClassName}>
      {items.map((item, index) => {
        const isSelected = index === selectedIndex;
        return (
          <li key={item.id}>
            <AudioRow
              title={item.title}
              index={index}
              isPlaying={isSelected && isPlaying}
              analyser={isSelected ? analyser : null}
              onToggle={() => handleToggle(index)}
            />
          </li>
        );
      })}
    </ul>
  );
}
